import React, { useState } from 'react';
import { Button, TextField, Box, Typography, Alert, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { authService } from '../../services/authService';

type InviteRole = 'admin' | 'worker';

const InviteCodeGenerator = () => {
  const [role, setRole] = useState<InviteRole>('worker');
  const [customCode, setCustomCode] = useState('');
  const [expiryDays, setExpiryDays] = useState(7);
  const [generatedCode, setGeneratedCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleGenerate = async () => {
    try {
      setError('');
      setSuccess('');
      
      const result = await authService.generateInviteCode({
        role,
        expiryDays,
        codeString: customCode || undefined
      });
      
      setGeneratedCode(result.inviteCode);
      setSuccess(`Invite code generated successfully! Code: ${result.inviteCode}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate invite code');
    }
  };

  return (
    <Box sx={{ p: 3, border: '1px solid #ddd', borderRadius: 2, maxWidth: 500 }}>
      <Typography variant="h6" gutterBottom>
        Generate Invite Code
      </Typography>
      
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Role</InputLabel>
        <Select
          value={role}
          onChange={(e) => setRole(e.target.value as InviteRole)}
          label="Role"
          size="small"
        >
          <MenuItem value="worker">Worker</MenuItem>
          <MenuItem value="admin">Admin</MenuItem>
        </Select>
      </FormControl>
      
      <TextField
        label="Custom Code (optional)"
        value={customCode}
        onChange={(e) => setCustomCode(e.target.value)}
        fullWidth
        size="small"
        sx={{ mb: 2 }}
      />
      
      <TextField
        label="Expiry Days"
        type="number"
        value={expiryDays}
        onChange={(e) => setExpiryDays(Number(e.target.value))}
        fullWidth
        size="small"
        inputProps={{ min: 1 }}
        sx={{ mb: 2 }}
      />
      
      <Button 
        variant="contained" 
        onClick={handleGenerate}
        fullWidth
        size="small"
      >
        Generate Invite Code
      </Button>
      
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mt: 2 }}>
          {success}
        </Alert>
      )}
      
      {generatedCode && (
        <Box sx={{ 
          mt: 2, 
          p: 2, 
          backgroundColor: 'background.paper',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider'
        }}>
          <Typography variant="body1" color="text.primary">
            <strong>Invite Code:</strong> {generatedCode}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
            Share this code with the new user to allow registration.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default InviteCodeGenerator;
